'use client'

import { useClientCountry } from "../i18n/ClientCountryProvider"
import { useLanguage } from "../i18n/LanguageProvider"
import { useEffect, useRef } from "react"
import useEventCountries from "@/hooks/cachedCalls/use-event-countries"
import { useCountrySelectionModal } from "../providers/CountrySelectionModalProvider"

const INITIALIZATION_KEY = 'countryModalInitialized'

const CountryInitializer = () => {
    const { currentLocale } = useLanguage()
    const { currentCountryData, changeCountryData, isLoadingCurrentCountryData } = useClientCountry()
    const { data: countries } = useEventCountries()
    const { openModal } = useCountrySelectionModal()
    
    // Usar useRef para rastrear si ya se ejecutó la inicialización en esta sesión
    const hasTriedToInitialize = useRef(false)
    const isInitializing = useRef(false)

    // Inicializar país y mostrar modal si no hay país seleccionado
    useEffect(() => {
        // Evitar múltiples ejecuciones
        if (hasTriedToInitialize.current || isInitializing.current) return
        if (isLoadingCurrentCountryData || typeof window === 'undefined') return
        if (!countries || countries.length === 0) return

        // Si el usuario ya tiene un país seleccionado, marcar como inicializado
        if (currentCountryData) {
            hasTriedToInitialize.current = true
            localStorage.setItem(INITIALIZATION_KEY, 'true')
            return
        }

        // Verificar si ya se inicializó en una sesión anterior
        const wasInitialized = localStorage.getItem(INITIALIZATION_KEY) === 'true'
        if (wasInitialized) {
            hasTriedToInitialize.current = true
            return
        }

        // Solo inicializar si NO hay país seleccionado y NO se ha inicializado antes
        isInitializing.current = true
        hasTriedToInitialize.current = true

        // Función para establecer el país por defecto
        const setDefaultCountry = (detectedCountryName?: string) => {
            let defaultCountry = null

            // Si tenemos el nombre del país detectado, buscar si está en la lista
            if (detectedCountryName) {
                defaultCountry = countries.find(country =>
                    country.value.toLowerCase() === detectedCountryName.toLowerCase()
                )
            }

            // Si no encontramos el país detectado, usar España como fallback
            if (!defaultCountry) {
                defaultCountry = countries.find(country => country.id.toString() === '65')
            }

            // Si España tampoco está disponible, usar el primer país de la lista
            if (!defaultCountry && countries.length > 0) {
                defaultCountry = countries[0]
            }

            if (defaultCountry) {
                changeCountryData({
                    name: defaultCountry.value,
                    id: defaultCountry.id.toString()
                })
            }

            isInitializing.current = false
        }

        // Intentar detectar el país por geolocalización
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}&localityLanguage=${currentLocale}`
                    const res = await fetch(url)
                    const data = await res.json()
                    setDefaultCountry(data.countryName)
                } catch (error) {
                    console.error('Error getting location:', error)
                    setDefaultCountry()
                }
            },
            (err) => {
                console.error('Geolocation error:', err)
                setDefaultCountry()
            }
        )

        // Marcar como inicializado y abrir el modal
        localStorage.setItem(INITIALIZATION_KEY, 'true')
        openModal()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingCurrentCountryData, countries?.length, currentCountryData])

    // Limpiar localStorage si el usuario elimina su selección de país manualmente
    useEffect(() => {
        // Solo limpiar si ya no estamos cargando y definitivamente no hay país
        if (!isLoadingCurrentCountryData && !currentCountryData) {
            const wasInitialized = localStorage.getItem(INITIALIZATION_KEY) === 'true'
            // Si estaba inicializado pero ahora no hay país, el usuario lo eliminó
            if (wasInitialized && hasTriedToInitialize.current && !isInitializing.current) {
                localStorage.removeItem(INITIALIZATION_KEY)
                hasTriedToInitialize.current = false
            }
        }
    }, [currentCountryData, isLoadingCurrentCountryData])

    // Este componente no renderiza nada visible
    return null
}

export default CountryInitializer
